import { create } from 'zustand';
import { TeamColor } from '../types/wargame-teams';

export type VictoryState = 'playing' | 'primary_won' | 'secondary_won' | 'failed' | 'game_complete';

export interface Objective {
  id: string;
  description: string;
  type: 'primary' | 'secondary';
  targetValue: number;
  currentValue: number;
  isMet: boolean;
}

export interface TeamScore {
  teamId: TeamColor;
  score: number;
  primaryObjective: Objective;
  secondaryObjective: Objective;
  strategicFailure: boolean;
  victoryState: VictoryState;
}

interface VictoryStore {
  teamScores: Map<TeamColor, TeamScore>;
  gameWinner: TeamColor | null;
  
  initializeObjectives: (teams: TeamColor[]) => void;
  updateTeamScore: (teamId: TeamColor, points: number) => void;
  checkVictoryConditions: () => void;
  getTeamScore: (teamId: TeamColor) => TeamScore | undefined;
  setStrategicFailure: (teamId: TeamColor) => void;
}

export const useVictoryStore = create<VictoryStore>((set, get) => ({
  teamScores: new Map(),
  gameWinner: null,

  initializeObjectives: (teams) => {
    const newScores = new Map<TeamColor, TeamScore>();
    teams.forEach(team => {
      newScores.set(team, {
        teamId: team,
        score: 0,
        primaryObjective: {
          id: 'primary_control',
          description: 'Control 3 Critical Sectors',
          type: 'primary',
          targetValue: 3,
          currentValue: 0,
          isMet: false
        },
        secondaryObjective: {
          id: 'secondary_influence',
          description: 'Achieve 30% Influence in 5 Communities',
          type: 'secondary',
          targetValue: 5,
          currentValue: 0,
          isMet: false
        },
        strategicFailure: false,
        victoryState: 'playing'
      });
    });
    set({ teamScores: newScores, gameWinner: null });
  },

  updateTeamScore: (teamId, points) => {
    const { teamScores } = get();
    const teamData = teamScores.get(teamId);
    if (teamData) {
      const newScores = new Map(teamScores);
      newScores.set(teamId, { ...teamData, score: teamData.score + points });
      set({ teamScores: newScores });
    }
  },

  checkVictoryConditions: () => {
    const { teamScores } = get();
    const newScores = new Map(teamScores);
    let winner: TeamColor | null = null;

    newScores.forEach((data, teamId) => {
      if (data.strategicFailure) return;

      // Check Primary
      if (data.primaryObjective.currentValue >= data.primaryObjective.targetValue) {
        data.primaryObjective.isMet = true;
        data.victoryState = 'primary_won';
        winner = teamId;
      }
      // Check Secondary (if primary not met but game ending?)
      // For now, simple check
      else if (data.secondaryObjective.currentValue >= data.secondaryObjective.targetValue) {
        data.secondaryObjective.isMet = true;
        // Secondary win doesn't end game immediately usually, but for simplicity:
        // data.victoryState = 'secondary_won'; 
      }
    });

    set({ teamScores: newScores, gameWinner: winner });
  },

  getTeamScore: (teamId) => get().teamScores.get(teamId),

  setStrategicFailure: (teamId) => {
    const { teamScores } = get();
    const teamData = teamScores.get(teamId);
    if (teamData) {
      const newScores = new Map(teamScores);
      newScores.set(teamId, { ...teamData, strategicFailure: true, victoryState: 'failed' });
      set({ teamScores: newScores });
    }
  }
}));
